import { useCallback, useEffect, useSyncExternalStore } from "react";
import { api } from "../lib/api";

export type AdminUser = {
  id: number;
  email: string;
  name: string | null;
};

// Store a nível de módulo (mesma estratégia do useUser): garante que AdminLogin
// notifique AdminNav na mesma janela após login bem-sucedido. Sem isso, Nav
// permanece com admin=null porque sua instância do hook nunca recebe update.
let cachedAdmin: AdminUser | null = null;
let cachedLoading = true;
let bootstrapped = false;
const subscribers = new Set<() => void>();

function notify(): void {
  subscribers.forEach((cb) => cb());
}

function subscribe(cb: () => void): () => void {
  subscribers.add(cb);
  return () => {
    subscribers.delete(cb);
  };
}

async function refreshAdmin(): Promise<void> {
  cachedLoading = true;
  notify();
  const r = await api<AdminUser>("/api/auth/admin/me");
  cachedAdmin = r.ok ? r.body : null;
  cachedLoading = false;
  notify();
}

export function useAdmin() {
  // Bootstrap único: primeira instância dispara fetch do /me. Subsequentes
  // só leem o cache compartilhado.
  useEffect(() => {
    if (!bootstrapped) {
      bootstrapped = true;
      void refreshAdmin();
    }
  }, []);

  const admin = useSyncExternalStore(
    subscribe,
    () => cachedAdmin,
    () => cachedAdmin
  );
  const loading = useSyncExternalStore(
    subscribe,
    () => cachedLoading,
    () => cachedLoading
  );

  const refresh = useCallback(async () => {
    await refreshAdmin();
  }, []);

  const logout = useCallback(async () => {
    await api("/api/auth/admin/logout", { method: "POST" });
    cachedAdmin = null;
    notify();
  }, []);

  return { admin, loading, refresh, logout };
}
